import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
    DEFAULT_TEMPERATURE_KEY,
    temperatures,
} from "@/pages/CalculationPage/sections/functional-properties-prediction/components/conditions/TemperatureSelect/TemperatureSelect.consts";
import { calculateIngredientAutoAdjustment } from "@/store/slices/formulation/formulation.utils";
import { Conditions, Formulation } from "@/types/formulation.types";
import { Ingredient } from "@/types/ingredient.types";

export type FormulationStoreState = Formulation & {
    autoAdjustIngredientId: number | null;
};

const INITIAL_STATE: FormulationStoreState = {
    conditions: {
        temperature: temperatures[DEFAULT_TEMPERATURE_KEY],
    },
    ingredients: [],
    autoAdjustIngredientId: null,
};

const formulationSlice = createSlice({
    name: "formulation",
    initialState: INITIAL_STATE,
    reducers: {
        setIngredients: (state, action: PayloadAction<Ingredient[]>) => {
            state.ingredients = action.payload;
        },
        setIngredientProportion: (state, action: PayloadAction<{ id: number; proportion: number }>) => {
            const ingredientIndex = state.ingredients.findIndex((ingredient) => ingredient.id === action.payload.id);
            if (ingredientIndex === -1) {
                return;
            }

            state.ingredients[ingredientIndex].currentProportion = action.payload.proportion;

            if (state.autoAdjustIngredientId !== null) {
                const autoAdjustIngredientIndex = state.ingredients.findIndex(
                    (ingredient) => ingredient.id === state.autoAdjustIngredientId
                );
                if (autoAdjustIngredientIndex === -1) {
                    return;
                }
                state.ingredients[autoAdjustIngredientIndex].currentProportion = calculateIngredientAutoAdjustment(
                    state.autoAdjustIngredientId,
                    state.ingredients
                );
            }
        },
        setAutoAdjustIngredient: (state, action: PayloadAction<{ id: number | null }>) => {
            if (action.payload.id === null) {
                state.autoAdjustIngredientId = null;
                return;
            }

            const autoAdjustIngredientIndex = state.ingredients.findIndex(
                (ingredient) => ingredient.id === action.payload.id
            );
            if (autoAdjustIngredientIndex === -1) {
                return;
            }
            state.autoAdjustIngredientId = action.payload.id;
            state.ingredients[autoAdjustIngredientIndex].currentProportion = calculateIngredientAutoAdjustment(
                action.payload.id,
                state.ingredients
            );
        },
        setFormulationStoreState: (_state, action: PayloadAction<FormulationStoreState>) => {
            return { ...action.payload };
        },
        updateConditions: (state, action: PayloadAction<Partial<Conditions>>) => {
            state.conditions = {
                ...state.conditions,
                ...action.payload,
            };
        },
    },
});

export const {
    setIngredients,
    setIngredientProportion,
    setAutoAdjustIngredient,
    setFormulationStoreState,
    updateConditions,
} = formulationSlice.actions;
export const formulationReducer = formulationSlice.reducer;
